import { vec3 } from 'gl-matrix';
import { atom } from 'recoil';
import { recoilPersist } from 'recoil-persist';
import { ViewMode } from '~/containers/patients-modal/LoadedPatients/ViewSwitcher';
import {
    ImageSlicePositions,
    PatientSpaceCoordsProps,
    ViewIndicesProps,
} from '~/containers/viewer/viewport/Viewport';
import { Orientations } from '~/engine/view/views/PlaneView';

const { persistAtom } = recoilPersist({
    key: 'viewerPersist',
    storage: localStorage,
    converter: JSON,
});

// REMOVE IN FUTURE: This is just a flag for export modal rendering purposes...
export const exportableItemsCountState = atom<number>({
    key: 'exportableItemsCountState',
    default: 0,
});

export const showFullscreenState = atom<boolean>({
    key: 'showFullscreenState',
    default: false,
});

export const loadingOverlayState = atom<{
    isOpen: boolean;
    message?: string;
}>({
    key: 'loadingOverlayState', // Unique key for this atom
    default: {
        isOpen: false,
    },
});

export const showSensitiveInfoState = atom<boolean>({
    // This atom is used to control the visibility of sensitive patient information e.g. patient name, patient ID, etc.
    key: 'showSensitiveInfoState',
    default: true,
});

export const explorerSidebarOpenState = atom({
    key: 'explorerSidebarOpenState',
    default: true,
});

export const datasourceModalOpenState = atom<boolean>({
    key: 'datasourceModalOpenState',
    default: false,
});

export const datasourceModalViewMode = atom<ViewMode>({
    key: 'datasourceModalViewModeState',
    default: 'tree',
});

export const autoLoadResultsState = atom<boolean>({
    key: 'autoLoadResultsState',
    default: true,
});

export enum DatasourceOptions {
    LOCAL,
    SERVER,
    REMOTE,
}

export const selectedDatasourceState = atom<DatasourceOptions>({
    key: 'selectedDatasourceState',
    default: DatasourceOptions.LOCAL,
});

// Three states: null for disabled DVH view, true for shown DVH view, false for hidden DVH view
export const showDVHViewComponentState = atom<boolean | null>({
    key: 'showDVHViewComponentState',
    default: null,
});

export const showDVHViewComponentFullScreenState = atom<boolean>({
    key: 'showDVHViewComponentFullScreenState',
    default: false,
});

export const showDoseStatisticsTableState = atom<boolean | null>({
    key: 'showDoseStatisticsTableState',
    default: null,
});

export const expandDoseStatisticsTableState = atom<boolean | null>({
    key: 'expandDoseStatisticsTableState',
    default: null,
});

export const showDoseStatisticsTableFullScreenState = atom<boolean | null>({
    key: 'showDoseStatisticsTableFullScreenState',
    default: null,
});

export enum WorkflowTypes {
    WORKSPACE,
    CONTOUR,
    DOSE,
    ADAPT,
    IMAGE,
    DATA_UPLOAD,
    SEG_VERIFY,
}

export const workflowModalState = atom<{
    open: boolean;
    page: WorkflowTypes;
}>({
    key: 'workflowModalState',
    default: {
        open: false,
        page: WorkflowTypes.CONTOUR,
    },
});

export const viewportOrientationOrderState = atom<Orientations[]>({
    key: 'viewportOrientationOrderState',
    default: [
        Orientations.TRANSVERSAL_ORIENTATION,
        Orientations.SAGITTAL_ORIENTATION,
        Orientations.CORONAL_ORIENTATION,
    ],
});

export const viewIndicesState = atom<ViewIndicesProps>({
    key: 'viewIndicesState',
    default: {
        TRANSVERSAL_ORIENTATION: null,
        SAGITTAL_ORIENTATION: null,
        CORONAL_ORIENTATION: null,
    },
});

export const coordinateDisplayExpandedState = atom<Orientations[] | null>({
    key: 'coordinateDisplayExpandedState',
    default: [Orientations.TRANSVERSAL_ORIENTATION],
});

export const imageValueAtCursorState = atom<number | null>({
    key: 'imageValueAtCursorState',
    default: null,
});

export const blendImageValueAtCursorState = atom<number | null>({
    key: 'blendImageValueAtCursorState',
    default: null,
});

export const doseIntensityValueAtCursorState = atom<number | null>({
    key: 'doseIntensityValueAtCursorState',
    default: null,
});

export const patientSpaceCoordsState = atom<PatientSpaceCoordsProps>({
    key: 'patientSpaceCoordsState',
    default: {
        coords: null,
        canvasCoords: null,
        orthoCoords: null,
    },
});

export const imageSlicesCountState = atom<number>({
    key: 'imageSlicesCountState',
    default: 0,
});

export const currentImageSliceOrientationPositionsState =
    atom<ImageSlicePositions>({
        key: 'currentImageSliceOrientationPositionsState',
        default: {
            TRANSVERSAL_ORIENTATION: '0',
            SAGITTAL_ORIENTATION: '0',
            CORONAL_ORIENTATION: '0',
        },
    });

export enum ExportTypes {
    STRUCTURES,
    DOSE,
}

export const exportDatasetsModalOpenState = atom<boolean>({
    key: 'exportDatasetsModalOpenState',
    default: false,
});

export const currenPositionAutoScrollState = atom<vec3 | null>({
    key: 'currenPositionAutoScrollState',
    default: null,
});

export const isAutoScrollingState = atom<boolean>({
    key: 'isAutoScrollingState',
    default: false,
});

export const pseudonymizeState = atom({
    key: 'pseudonymizeState',
    default: true,
});

export const enableExportMonacoDirFileState = atom<boolean>({
    key: 'enableExportMonacoDirFileState',
    default: false,
    effects: [persistAtom],
});
